<template>
  <div class="invoices-do-cliente-table">
    <div class="tab-content">
      <data-table
        :config="tableConfig"
        :data="tableData"
        :filterConfig="filterConfig"
        :refreshButton="refreshButton"
        @edit-click="handleEditClick"
        @refresh="loadData">
      </data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { usePaymentInvoices, type InvoiceListRow } from '../../../composables/usePaymentInvoices';

type Id = number | string;

// Backend row shape comes from the contract. UI-only extras (`ID` for grid
// row identity, `_isFrozenFooter` for footer rendering) are layered on top.
interface InvoiceRow extends Partial<InvoiceListRow> {
  ID?: Id;
  _isFrozenFooter?: boolean;
}

interface CellParams {
  value: unknown;
  data: InvoiceRow;
}

// Props
const props = defineProps<{
  clienteId: Id;
  franquiaId: number;
}>();

// Composables
const { t } = useI18n();
const { listInvoices, getInvoiceStatusConfig } = usePaymentInvoices();

// Reactive data
const tableData = ref<InvoiceRow[]>([]);

// Computed
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaInvoicesCliente',
    pageLength: 100,
    showFilters: false,
    sortable: false,
    columns: tableColumns.value,
    pagination: false,
    stripedRows: true,
    showGridlines: true,
    resizableColumns: true,
    size: 'small',
    heightMode: 'fill-parent'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {}
  };
});

const refreshButton = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id',
      width: 100
    },
    {
      data: 'invoice_number',
      title: t('Número'),
      width: 150
    },
    {
      data: 'status',
      title: t('Status'),
      width: 200,
      cellRenderer: (params: CellParams): string => {
        // For frozen footer row, show raw value
        if (params.data && params.data._isFrozenFooter) {
          return params.value != null ? String(params.value) : '';
        }

        const config = getInvoiceStatusConfig(params.value as string | null | undefined);
        return `<span class='p-tag p-tag-${config.severity}'><i class='pi ${config.icon} p-tag-icon'></i> <span class="p-tag-value">${config.label}</span></span>`;
      }
    },
    {
      data: 'total_amount',
      title: t('Valor'),
      width: 150,
      renderType: 'currency'
    },
    {
      data: 'emission_date',
      title: t('Data de Emissão'),
      width: 180,
      renderType: 'date'
    }
  ];
});

// Methods
// Clicking the invoice id opens the invoice detail modal (centralized in ModalManager).
const handleEditClick = (invoiceId: Id): void => {
  log('InvoicesDoClienteTable', 'handleEditClick', 'Opening invoice', invoiceId);
  eventBus.emit('modal.invoices.open', { id: invoiceId });
};

const loadData = async (): Promise<void> => {
  log('InvoicesDoClienteTable', 'loadData', 'Loading invoices for client', props.clienteId);

  if (!props.clienteId || props.clienteId === 0) {
    tableData.value = [];
    return;
  }

  try {
    const result = await listInvoices({
      cliente_id: Number(props.clienteId),
      franchise_id: props.franquiaId
    });
    tableData.value = result.invoices || [];
    log('InvoicesDoClienteTable', 'loadData', 'Loaded records', tableData.value.length);
  } catch (error) {
    log('InvoicesDoClienteTable', 'loadData', 'Error loading invoices', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar faturas: ') + message);
    tableData.value = [];
  }
};

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.invoices-do-cliente-table {
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tab-content {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
</style>
